import ButtonCommon from "@lhd/components/ButtonCommon";
import type { DisplaySize } from "@lhd/constants";
import { useFetchWorkerLogtimesMonthlyV2 } from "@lhd/hooks";
import type { WorkerLogtimeMonthlyV2Response } from "@lhd/hooks";
import { notification } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { parseMonthlyPdfData } from "./utility";
import { StylesTable } from "./style";

interface IProps {
  displaySize: DisplaySize;
  companyId: string | number;
  year: number;
  month: number;
}

const usePrintPdf = ({ displaySize, companyId, year, month }: IProps) => {
  const requestInFlight = useRef(false);
  const printTriggered = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [dataToPrint, setDataToPrint] =
    useState<WorkerLogtimeMonthlyV2Response>();
  const [isPrinting, setIsPrinting] = useState(false);
  const canFetch =
    !!companyId &&
    Number.isInteger(year) &&
    year > 0 &&
    Number.isInteger(month) &&
    month >= 1 &&
    month <= 12;

  const { isFetching, refetch } = useFetchWorkerLogtimesMonthlyV2(
    { company_id: companyId, year, month, page: 1, per_page: 9999 },
    false,
  );

  const { columns, rows } = useMemo(
    () => parseMonthlyPdfData(dataToPrint),
    [dataToPrint],
  );

  const print = useReactToPrint({
    contentRef,
    documentTitle: dataToPrint
      ? `${dataToPrint.company_name}_${dataToPrint.year}年${dataToPrint.month}月_勤怠一覧`
      : "勤怠一覧",
    pageStyle: `
      @page {
        size: A4 landscape;
        padding: 2mm;
      }

      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
    onAfterPrint: () => {
      printTriggered.current = false;
      setDataToPrint(undefined);
      setIsPrinting(false);
    },
    onPrintError: () => {
      printTriggered.current = false;
      setDataToPrint(undefined);
      setIsPrinting(false);
      notification.error({ message: "PDFの印刷に失敗しました。" });
    },
  });

  useEffect(() => {
    if (!dataToPrint || !contentRef.current || printTriggered.current) return;
    printTriggered.current = true;
    print();
  }, [dataToPrint, print]);

  const handlePrintPdf = async () => {
    if (!canFetch || requestInFlight.current || isPrinting) return;
    requestInFlight.current = true;
    printTriggered.current = false;
    setIsPrinting(true);
    try {
      const result = await refetch({ throwOnError: true });
      if (!result.data) throw new Error("No PDF data");
      setDataToPrint(result.data);
    } catch {
      printTriggered.current = false;
      setIsPrinting(false);
      notification.error({ message: "PDF用のデータ取得に失敗しました。" });
    } finally {
      requestInFlight.current = false;
    }
  };

  const btnPrint = (
    <div>
      <ButtonCommon
        name="PDF"
        displaySize={displaySize}
        className="non_css"
        disabled={!canFetch || isFetching || isPrinting}
        loading={isFetching || isPrinting}
        onClick={handlePrintPdf}
      />
    </div>
  );

  const elementPrint = dataToPrint ? (
    <StylesTable ref={contentRef} aria-hidden="true">
      <div>
        <h2>
          {dataToPrint.company_name} {dataToPrint.year}年{dataToPrint.month}月
          {" 勤怠一覧"}
        </h2>
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.dataKey}
                  scope="col"
                  style={{ ...column.style, textAlign: "center" }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column.dataKey} style={column.style}>
                    {row[column.dataKey]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </StylesTable>
  ) : null;
  return {
    btnPrint,
    elementPrint,
  };
};

export default usePrintPdf;
